// Selectors / constants used across the section controller.
const SECTION_SELECTOR = '[data-agency-process]';
const TAB_BUTTON_SELECTOR = '[data-agency-process-tab-button]';
const TAB_PANEL_SELECTOR = '[data-agency-process-panel]';
const COLUMN_SELECTOR = '[data-agency-process-column]';
const COLUMN_DESCRIPTION_SELECTOR = '[data-agency-process-description-source]';
const MOBILE_DESC_WRAPPER_SELECTOR = '[data-agency-process-mobile-desc]';
const MOBILE_DESC_LINE_SELECTOR = '[data-agency-process-mobile-desc-line]';
const MOBILE_DESC_CONTENT_SELECTOR = '[data-agency-process-mobile-desc-content]';
const DESC_AREA_SELECTOR = '[data-agency-process-description]';
const DESC_BODY_SELECTOR = '[data-agency-process-description-body]';
const DESC_LINE_SELECTOR = '[data-agency-process-desc-line]';
const DESC_CONTENT_SELECTOR = '[data-agency-process-desc-content]';
const SVG_PATH_SELECTOR = '[data-agency-process-path]';
const CUSTOM_EASE = 'cubic-bezier(.98,.09,.15,.96)';
const DESC_EXIT_PERCENT = 150;
const DESC_EXIT_DURATION = 0.15;
const DESC_ENTER_DURATION = 0.35;
const DESC_LINE_DURATION = 0.25;
const DESC_EXIT_EASE = 'cubic-bezier(0.16, 0.77, 0.21, 0.93)';
const DESC_ENTER_EASE = 'cubic-bezier(0.21, 0.32, 0.03, 0.95)';

const instances = new Map(); // Track section instances for hot reloads.
const dependencyQueue = []; // Sections waiting on gsap/ScrollTrigger to load.
let dependencyTimer = null;
let dependencyLogPrinted = false;
const DEPENDENCY_CHECK_INTERVAL = 100;
const DEPENDENCY_MAX_ATTEMPTS = 100;

class AgencyProcess {
  constructor(section) {
    this.section = section;
    this.gsap = window.gsap || null;
    this.hasGsap = Boolean(this.gsap);
    // GSAP + ScrollTrigger references
    // ScrollTrigger might be attached either via gsap.ScrollTrigger or globally.
    this.ScrollTrigger = (this.gsap && this.gsap.ScrollTrigger) || window.ScrollTrigger || null;
    if (this.gsap && this.ScrollTrigger) {
      this.gsap.registerPlugin(this.ScrollTrigger);
    }

    // Cache important DOM references for faster lookups.
    this.tabButtons = Array.from(section.querySelectorAll(TAB_BUTTON_SELECTOR));
    this.panels = Array.from(section.querySelectorAll(TAB_PANEL_SELECTOR));
    this.panelData = new Map();
    this.columnData = new Map();
    this.listeners = [];
    this.activeTabIndex = -1;
    this.activePanel = null; 
    this.isAnimating = false;
    this.motionQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    this.modeMedia = window.matchMedia('(max-width: 900px)');
    this.currentMode = null;
    this.desktopActive = false;
    this.mobileModeActive = false;
    this.mobileOpenHandlers = [];

    this.addSectionListener('mouseleave', () => this.onSectionLeave());
    this.registerTabButtons();
    this.panels.forEach((panel) => this.setupPanel(panel));

    if (this.panels.length) {
      this.showTab(0, false);
    }

    this.debug('AgencyProcess initialized', {
      hasGsap: this.hasGsap,
      hasScrollTrigger: Boolean(this.ScrollTrigger),
      panelCount: this.panels.length,
      columnCount: this.panels.reduce((acc, panel) => acc + panel.querySelectorAll(COLUMN_SELECTOR).length, 0),
    });

    this.modeHandler = () => this.setMode(); // handles mq change events
    this.debouncedResize = this.debounce(() => this.setMode(), 200); // catch resize drags
    this.setMode();
    this.modeMedia.addEventListener('change', this.modeHandler);
    window.addEventListener('resize', this.debouncedResize);
  }

  debug(message, payload) {
    const sectionId = this.section?.dataset?.sectionId || 'unknown';
    if (typeof payload !== 'undefined') {
      console.log(`[AgencyProcess][${sectionId}] ${message}`, payload);
    } else {
      console.log(`[AgencyProcess][${sectionId}] ${message}`);
    }
  }

  getColumnLabel(column) {
    if (!column) return 'unknown-column';
    const textEl = column.querySelector('.agency-process__col-title-text');
    if (textEl && textEl.textContent) {
      return textEl.textContent.trim();
    }
    return column.dataset?.tabTitle || `column-${column.dataset?.tabIndex || 'unknown'}`;
  }

  addSectionListener(eventName, handler) {
    this.section.addEventListener(eventName, handler);
    this.listeners.push({ element: this.section, event: eventName, handler });
  }

  addListener(element, eventName, handler) {
    element.addEventListener(eventName, handler);
    this.listeners.push({ element, event: eventName, handler });
  }

  registerTabButtons() {
    this.tabButtons.forEach((button) => {
      const handler = (event) => {
        const index = Number(button.dataset.tabIndex || 0);
        this.showTab(index, true);
        if (event && event.detail !== 0 && typeof button.blur === 'function') {
          button.blur();
        }
      };
      this.addListener(button, 'click', handler);
    });
  }

  setupPanel(panel) {
    const svgPathEl = panel.querySelector(SVG_PATH_SELECTOR);
    const descArea = panel.querySelector(DESC_AREA_SELECTOR);
    const descContent = panel.querySelector(DESC_CONTENT_SELECTOR);
    const descBody = panel.querySelector(DESC_BODY_SELECTOR);
    const descLine = panel.querySelector(DESC_LINE_SELECTOR);
    const panelInfo = {
      panel,
      columns: [],
      svgPathEl,
      svgPathLength: null,
      descArea,
      descContent,
      descBody,
      descLine,
      descLineHeight: null,
      activeColumn: null,
      descTimeline: null,
      descInitialized: false,
    };

    const svgPathValue = panel.dataset.svgPath;
    if (svgPathEl) {
      if (typeof svgPathValue === 'string' && svgPathValue.length) {
        svgPathEl.setAttribute('d', svgPathValue);
      }
      try {
        const length = svgPathEl.getTotalLength();
        panelInfo.svgPathLength = length;
        if (this.hasGsap) {
          this.gsap.set(svgPathEl, {
            strokeDasharray: length,
            strokeDashoffset: length,
          });
        } else {
          svgPathEl.style.strokeDasharray = length;
          svgPathEl.style.strokeDashoffset = length;
        }
      } catch (error) {
        panelInfo.svgPathLength = null;
      }
    }

    const columns = Array.from(panel.querySelectorAll(COLUMN_SELECTOR));
    columns.forEach((column) => {
      panelInfo.columns.push(column);
      this.storeColumnData(column);
      this.registerColumnEvents(panel, column);
      if (!column.hasAttribute('tabindex')) {
        column.setAttribute('tabindex', '0');
      }
    });

    this.panelData.set(panel, panelInfo);

    const defaultColumn = columns.find((col) => col.classList.contains('is-active')) || columns[0];
    if (defaultColumn) {
      this.setActiveColumn(panel, defaultColumn, false);
    } else if (descBody) {
      descBody.innerHTML = '';
    }

    if (descLine && descContent) {
      const initialHeight = this.getDescLineHeight(panelInfo);
      this.setDescLineHeight(panelInfo, initialHeight);
    }
  }

  storeColumnData(column) {
    const descriptionEl = column.querySelector(COLUMN_DESCRIPTION_SELECTOR);
    const accent = column.dataset.accentColor || column.style.getPropertyValue('--agency-process-accent') || '';
    const mobileDescWrapper = column.querySelector(MOBILE_DESC_WRAPPER_SELECTOR);
    const mobileDescLine = column.querySelector(MOBILE_DESC_LINE_SELECTOR);
    const mobileDescContent = column.querySelector(MOBILE_DESC_CONTENT_SELECTOR);
    const titleText = column.querySelector('.agency-process__col-title-text');

    this.columnData.set(column, {
      descriptionEl,
      accent,
      mobile: {
        wrapper: mobileDescWrapper,
        line: mobileDescLine,
        content: mobileDescContent,
        trigger: null,
        titleText,
        defaultTitleColor: '',
      },
    });
  }

  registerColumnEvents(panel, column) {
    const enterHandler = () => this.onColumnEnter(panel, column);
    const leaveHandler = () => this.onColumnLeave(column);
    const focusHandler = () => this.onColumnEnter(panel, column);
    const blurHandler = (event) => {
      if (!column.contains(event.relatedTarget)) {
        this.onColumnLeave(column);
      }
    };

    this.addListener(column, 'mouseenter', enterHandler);
    this.addListener(column, 'mouseleave', leaveHandler);
    this.addListener(column, 'focusin', focusHandler);
    this.addListener(column, 'focusout', blurHandler);
  }

  onColumnEnter(panel, column) {
    column.classList.add('is-hovered');
    this.setActiveColumn(panel, column, true);
  }

  onColumnLeave(column) {
    column.classList.remove('is-hovered');
  }

  onSectionLeave() {
    this.panelData.forEach((info) => {
      info.columns.forEach((column) => {
        column.classList.remove('is-hovered');
      });
    });
  }

  setActiveColumn(panel, column, fromHover) {
    const info = this.panelData.get(panel);
    if (!info || !column) return;

    if (info.activeColumn === column) {
      if (fromHover) {
        column.classList.add('is-hovered');
      }
      return;
    }

    if (info.activeColumn) {
      info.activeColumn.classList.remove('is-active');
    }

    info.activeColumn = column;
    column.classList.add('is-active');

    this.updateDescription(info, column);
  }

  updateDescription(info, column) {
    if (!info.descArea || !info.descBody) return;

    const columnInfo = this.columnData.get(column) || {};
    const accent = column.dataset.accentColor || columnInfo.accent || 'var(--color-accent)';
    const nextContent = columnInfo.descriptionEl ? columnInfo.descriptionEl.innerHTML : '';
    const hasLine = Boolean(info.descLine);
    const prevLineHeight = hasLine ? info.descLineHeight ?? this.getDescLineHeight(info) : null;

    if (accent) {
      info.descArea.style.setProperty('--agency-process-desc-accent', accent);
    }

    const shouldAnimate = this.hasGsap && !this.prefersReducedMotion() && info.descInitialized;

    if (!shouldAnimate) {
      if (info.descTimeline) {
        info.descTimeline.kill();
        info.descTimeline = null;
      }
      info.descBody.innerHTML = nextContent;
      if (this.hasGsap) {
        this.gsap.set(info.descBody, { yPercent: 0, autoAlpha: 1 });
      }
      if (hasLine) {
        const newHeight = this.getDescLineHeight(info);
        this.setDescLineHeight(info, newHeight);
      }
      info.descInitialized = true;
      return;
    }

    if (hasLine && prevLineHeight != null) {
      this.setDescLineHeight(info, prevLineHeight);
    }

    if (info.descTimeline) {
      info.descTimeline.kill();
      info.descTimeline = null;
    }

    const timeline = this.gsap.timeline({
      defaults: { ease: CUSTOM_EASE },
      onComplete: () => {
        info.descTimeline = null;
      },
    });

    info.descTimeline = timeline;

    timeline.to(info.descBody, {
      yPercent: DESC_EXIT_PERCENT,
      autoAlpha: 0.1,
      duration: DESC_EXIT_DURATION,
      ease: DESC_EXIT_EASE,
    });

    timeline
      .add(() => {
        info.descBody.innerHTML = nextContent;
        if (hasLine) {
          info.descLineHeight = this.getDescLineHeight(info);
        }
      })
      .set(info.descBody, { yPercent: DESC_EXIT_PERCENT, autoAlpha: 1 })
      .to(info.descBody, { yPercent: 0, duration: DESC_ENTER_DURATION, ease: DESC_ENTER_EASE }, '>');

    if (hasLine) {
      timeline.to(
        info.descLine,
        {
          height: () => `${info.descLineHeight != null ? info.descLineHeight : prevLineHeight || 0}px`,
          duration: DESC_LINE_DURATION,
          ease: CUSTOM_EASE,
        },
        '-=0.15',
      );
    }

    info.descInitialized = true;
  }

  getDescLineHeight(info) {
    if (!info || !info.descContent) return 0;
    return info.descContent.offsetHeight || 0;
  }

  setDescLineHeight(info, height) {
    if (!info || !info.descLine || typeof height !== 'number' || Number.isNaN(height)) {
      return;
    }
    const nextHeight = Math.max(0, height);
    info.descLine.style.height = `${nextHeight}px`;
    info.descLineHeight = nextHeight;
  }

  prefersReducedMotion() {
    return this.motionQuery ? this.motionQuery.matches : false;
  }

  async showTab(index, animate) {
    if (typeof index !== 'number' || index < 0 || index >= this.panels.length) {
      return;
    }

    if (this.isAnimating || this.activeTabIndex === index) {
      return;
    }

    const nextPanel = this.panels[index];
    const currentPanel = this.activePanel;

    this.isAnimating = true;
    this.activeTabIndex = index;
    this.updateTabButtons(index);

    if (currentPanel) {
      if (this.desktopActive) {
        await this.animateTabOut(currentPanel, animate);
      }
      currentPanel.classList.remove('is-active');
      currentPanel.setAttribute('aria-hidden', 'true');
    }

    nextPanel.classList.add('is-active');
    nextPanel.setAttribute('aria-hidden', 'false');
    this.activePanel = nextPanel;

    if (this.desktopActive) {
      await this.animateTabIn(nextPanel, animate);
    }

    this.isAnimating = false;
  }

  updateTabButtons(activeIndex) {
    this.tabButtons.forEach((button) => {
      const index = Number(button.dataset.tabIndex || 0);
      const isActive = index === activeIndex;
      button.setAttribute('aria-selected', String(isActive));
      button.classList.toggle('primary', isActive);
      button.classList.toggle('secondary', !isActive);
    });
  }

  animateTabOut(panel, animate) {
    const info = this.panelData.get(panel);
    if (!info) return Promise.resolve();
    if (!this.hasGsap) return Promise.resolve();

    const columns = info.columns;
    const titles = columns
      .map((column) => column.querySelector('.agency-process__col-title-text'))
      .filter(Boolean);
    const desc = info.descArea;
    const pathEl = info.svgPathEl;
    const pathLength = info.svgPathLength;

    columns.forEach((column) => column.classList.remove('is-hovered'));

    if (!animate || this.prefersReducedMotion()) {
      if (titles.length) {
        this.gsap.set(titles, { autoAlpha: 0, yPercent: 100 });
      }
      if (desc) {
        this.gsap.set(desc, { autoAlpha: 0, y: 20 });
      }
      if (pathEl && pathLength != null) {
        this.gsap.set(pathEl, { strokeDashoffset: pathLength });
      }
      if (columns.length) {
        this.gsap.set(columns, { autoAlpha: 0 });
      }
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const timeline = this.gsap.timeline({
        defaults: { duration: 0.35, ease: 'power2.out' },
        onComplete: resolve,
      });

      if (titles.length) {
        timeline.to(titles, { yPercent: 100, autoAlpha: 0, stagger: 0.05 });
      }

      if (desc) {
        timeline.to(desc, { autoAlpha: 0, y: 20 }, titles.length ? '-=0.2' : 0);
      }

      if (pathEl && pathLength != null) {
        timeline.to(pathEl, { strokeDashoffset: pathLength, duration: 0.55, ease: CUSTOM_EASE }, '-=0.2');
      }

      if (columns.length) {
        timeline.to(columns, { autoAlpha: 0 }, '-=0.15');
      }
    });
  }

  animateTabIn(panel, animate) {
    const info = this.panelData.get(panel);
    if (!info) return Promise.resolve();
    if (!this.hasGsap) return Promise.resolve();

    const columns = info.columns;
    const titles = columns
      .map((column) => column.querySelector('.agency-process__col-title-text'))
      .filter(Boolean);
    const desc = info.descArea;
    const pathEl = info.svgPathEl;
    const pathLength = info.svgPathLength;
    const activeColumn = info.activeColumn || columns[0];

    if (activeColumn) {
      this.setActiveColumn(panel, activeColumn, false);
    }

    if (!animate || this.prefersReducedMotion()) {
      if (titles.length) {
        this.gsap.set(titles, { autoAlpha: 1, yPercent: 0 });
      }
      if (desc) {
        this.gsap.set(desc, { autoAlpha: 1, y: 0 });
      }
      if (pathEl && pathLength != null) {
        this.gsap.set(pathEl, { strokeDashoffset: 0, strokeDasharray: pathLength });
      }
      if (columns.length) {
        this.gsap.set(columns, { autoAlpha: 1 });
      }
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const timeline = this.gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: resolve,
      });

      if (columns.length) {
        this.gsap.set(columns, { autoAlpha: 0 });
        timeline.to(columns, { autoAlpha: 1, duration: 0.4 });
      }

      if (titles.length) {
        this.gsap.set(titles, { autoAlpha: 0, yPercent: 100 });
        timeline.to(titles, { autoAlpha: 1, yPercent: 0, duration: 0.45, stagger: 0.08 }, '-=0.25');
      }

      if (desc) {
        this.gsap.set(desc, { autoAlpha: 0, y: 24 });
        timeline.to(desc, { autoAlpha: 1, y: 0, duration: 0.45 }, '-=0.3');
      }

      if (pathEl && pathLength != null) {
        this.gsap.set(pathEl, { strokeDashoffset: pathLength, strokeDasharray: pathLength });
        timeline.to(pathEl, { strokeDashoffset: 0, duration: 0.9, ease: CUSTOM_EASE }, '-=0.35');
      }
    });
  }

  destroy() {
    if (this.modeMedia) {
      this.modeMedia.removeEventListener('change', this.modeHandler);
    }
    if (this.debouncedResize) {
      window.removeEventListener('resize', this.debouncedResize);
    }
    if (this.currentMode === 'mobile') {
      this.destroyMobileMode();
    }
    if (this.currentMode === 'desktop') {
      this.destroyDesktopMode();
    }
    this.modeMedia.removeEventListener('change', this.modeHandler);
    window.removeEventListener('resize', this.debouncedResize);

    this.listeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.listeners = [];

    this.panelData.clear();
    this.columnData.clear();
    this.activePanel = null;
  }

  setMode() {
    const mobile = this.modeMedia.matches;
    const newMode = mobile ? 'mobile' : 'desktop';
    if (newMode === this.currentMode) {
      this.debug('setMode aborted; already in requested mode', { mode: newMode });
      return;
    }

    this.debug('setMode change', { from: this.currentMode, to: newMode });

    // Tear down whichever mode was previously active
    if (this.currentMode === 'mobile') {
      this.destroyMobileMode();
    }
    if (this.currentMode === 'desktop') {
      this.destroyDesktopMode();
    }

    if (newMode === 'mobile') {
      this.initMobileMode();
    } else {
      this.initDesktopMode();
    }

    this.currentMode = newMode;
  }
 
  initDesktopMode() {
    this.desktopActive = true;
    const initialIndex = this.activeTabIndex >= 0 ? this.activeTabIndex : 0;
    this.activeTabIndex = -1;
    this.showTab(initialIndex, false);
  }

  destroyDesktopMode() {
    this.desktopActive = false;
    this.panelData.forEach((info) => {
      if (info.descTimeline) {
        info.descTimeline.kill();
        info.descTimeline = null;
      }
      info.descInitialized = false;
    });
  } 

  initMobileMode() {
    // Mobile accordion mode uses ScrollTrigger + GSAP timelines to open columns
    // when they enter the viewport and close them when they exit.
    if (!this.hasGsap || !this.ScrollTrigger) {
      this.debug('initMobileMode aborted due to missing GSAP/ScrollTrigger', {
        hasGsap: this.hasGsap,
        hasScrollTrigger: Boolean(this.ScrollTrigger),
      });
      return;
    }
    this.debug('initMobileMode start', {
      panels: this.panels.length,
      totalColumns: this.panels.reduce((sum, panel) => sum + (this.panelData.get(panel)?.columns.length || 0), 0),
    });
    this.mobileModeActive = true;
    this.panelData.forEach((panelInfo) => {
      const panelIndex = this.panels.indexOf(panelInfo.panel);
      panelInfo.columns.forEach((column, index) => {
        const columnInfo = this.columnData.get(column);
        if (!columnInfo || !columnInfo.mobile || !columnInfo.mobile.wrapper) {
          this.debug('Skipping column – missing mobile wrapper', {
            panelIndex,
            columnIndex: index,
            columnLabel: this.getColumnLabel(column),
            hasColumnInfo: Boolean(columnInfo),
          });
          return;
        }
        const { wrapper, line } = columnInfo.mobile;
        this.debug('Configuring mobile column accordion', {
          panelIndex,
          columnIndex: index,
          columnLabel: this.getColumnLabel(column),
          hasLine: Boolean(line),
        });
        if (wrapper) {
          wrapper.classList.add('is-ready');
          this.gsap.set(wrapper, { opacity: 0.3 });
        }
        if (line) {
          this.gsap.set(line, { scaleY: 0, transformOrigin: 'top left' });
        }
        if (columnInfo.mobile.titleText) {
          const defaultColor = window.getComputedStyle(columnInfo.mobile.titleText).color;
          columnInfo.mobile.defaultTitleColor = defaultColor;
          this.gsap.set(columnInfo.mobile.titleText, { color: defaultColor });
        }
        this.setMobileColumnActive(column, false, true);

        const triggerElement = column.querySelector('.agency-process__col-title') || column;
        const columnLabel = this.getColumnLabel(column);
        const triggerStart = 'top 45%'; // open once the title is well inside the viewport
        this.debug('Creating ScrollTrigger for mobile column', {
          panelIndex,
          columnIndex: index,
          columnLabel,
          start: triggerStart,
          triggerElementTag: triggerElement?.tagName,
          triggerHasTitle: Boolean(column.querySelector('.agency-process__col-title')),
        });
        columnInfo.mobile.trigger = this.ScrollTrigger.create({
          trigger: triggerElement,
          start: triggerStart,
          // User is scrolling down and the column crosses the entry threshold.
          onEnter: (self) => {
            this.debug('ScrollTrigger threshold reached', {
              direction: 'enter',
              columnIndex: index,
              columnLabel,
              start: self?.start,
              end: self?.end,
              progress: self?.progress,
            });
            this.setMobileColumnActive(column, true);
          },
          // When scrolling upward back past the trigger, collapse the column.
          onLeaveBack: (self) => {
            this.debug('ScrollTrigger leave (back)', {
              direction: 'leaveBack',
              columnIndex: index,
              columnLabel,
              start: self?.start,
              end: self?.end,
              progress: self?.progress,
            });
            this.setMobileColumnActive(column, false);
          },
        });
      });
    });
    this.debug('initMobileMode complete – refreshing ScrollTrigger', {});
    this.ScrollTrigger.refresh();
  }

  destroyMobileMode() {
    if (!this.mobileModeActive) return;
    // Tear down timelines + triggers so desktop mode or hot reloads don't leak.
    this.panelData.forEach((panelInfo) => {
      panelInfo.columns.forEach((column) => {
        const columnInfo = this.columnData.get(column);
        if (!columnInfo || !columnInfo.mobile) return;
        const { wrapper, line, trigger, titleText } = columnInfo.mobile;
        if (trigger) {
          trigger.kill();
          columnInfo.mobile.trigger = null;
        }
        if (wrapper) {
          wrapper.style.removeProperty('opacity');
          wrapper.classList.remove('is-ready');
        }
        if (line) {
          line.style.removeProperty('height');
          line.style.removeProperty('transform');
        }
        if (titleText) {
          titleText.style.removeProperty('color');
        }
        columnInfo.mobile.defaultTitleColor = '';
      });
    });
    this.mobileModeActive = false; 
    if (this.ScrollTrigger) {
      this.ScrollTrigger.refresh();
    }
  }

  setMobileColumnActive(column, isActive, immediate = false) {
    const columnInfo = this.columnData.get(column);
    if (!columnInfo?.mobile) return;
    const { wrapper, line, titleText, defaultTitleColor } = columnInfo.mobile;
    const accentColor = column.dataset.accentColor || columnInfo.accent || 'var(--color-accent)';
    const duration = immediate ? 0 : 0.35;

    if (wrapper) {
      this.gsap.to(wrapper, {
        opacity: isActive ? 1 : 0.3,
        duration,
        overwrite: true,
        ease: 'power2.out',
      });
    }

    if (line) {
      this.gsap.to(line, {
        scaleY: isActive ? 1 : 0,
        duration,
        ease: 'power2.out',
        overwrite: true,
      });
    }

    if (titleText) {
      const targetColor = isActive ? accentColor : defaultTitleColor || '';
      if (targetColor) {
        this.gsap.to(titleText, {
          color: targetColor,
          duration,
          overwrite: true,
          ease: 'power2.out',
        });
      }
    }
  }


  debounce(fn, delay = 200) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

function dependenciesAvailable() {
  return Boolean(window.gsap && (window.gsap.ScrollTrigger || window.ScrollTrigger));
}

// Run any queued initializers after GSAP/ScrollTrigger is available (or if timeout expires).
function flushDependencyQueue() {
  if (!dependencyQueue.length) return;
  if (dependenciesAvailable()) {
    if (dependencyLogPrinted) {
      console.log('[AgencyProcess] GSAP/ScrollTrigger detected. Initializing queued sections.');
      dependencyLogPrinted = false;
    }
  } else if (dependencyLogPrinted) {
    console.warn('[AgencyProcess] Continuing initialization without GSAP/ScrollTrigger due to timeout.');
    dependencyLogPrinted = false;
  }
  while (dependencyQueue.length) {
    const callback = dependencyQueue.shift();
    try {
      callback();
    } catch (error) {
      console.error('[AgencyProcess] Failed to initialize section after waiting for dependencies', error);
    }
  }
}

// Poll for GSAP/ScrollTrigger so sections initialized before the scripts load still boot.
function startDependencyWatcher() {
  if (dependencyTimer != null) return;
  let attempts = 0;
  dependencyTimer = window.setInterval(() => {
    if (dependenciesAvailable()) {
      window.clearInterval(dependencyTimer);
      dependencyTimer = null;
      flushDependencyQueue();
      return;
    }
    attempts += 1;
    if (attempts >= DEPENDENCY_MAX_ATTEMPTS) {
      console.warn('[AgencyProcess] GSAP/ScrollTrigger not detected after waiting; initializing without them.');
      window.clearInterval(dependencyTimer);
      dependencyTimer = null;
      flushDependencyQueue();
    }
  }, DEPENDENCY_CHECK_INTERVAL);
}

// Either run immediately or enqueue until the dependency watcher flushes the queue.
function runWhenDependenciesReady(callback) {
  if (dependenciesAvailable()) {
    callback();
    return;
  }
  dependencyQueue.push(callback);
  if (!dependencyLogPrinted) {
    dependencyLogPrinted = true;
    console.log('[AgencyProcess] Waiting for GSAP/ScrollTrigger before initializing section scripts.');
  }
  startDependencyWatcher();
}

function initSection(section) {
  if (!section) return;
  // Delay initialization until dependencies are ready; Shopify hot reload may re-run this.
  const start = () => {
    if (!section.isConnected) {
      return;
    }
    const instance = new AgencyProcess(section);
    instances.set(section.dataset.sectionId, instance);
  };
  runWhenDependenciesReady(start);
}

function destroySection(id) {
  const instance = instances.get(id);
  if (!instance) return;
  instance.destroy();
  instances.delete(id);
}

function initAll() {
  // Initialize every matching section on the page (handles static + dynamic renders).
  document.querySelectorAll(SECTION_SELECTOR).forEach((section) => initSection(section));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector(SECTION_SELECTOR);
  if (section) {
    initSection(section);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  destroySection(event.detail.sectionId);
});
